import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import FadeIn from './shared/FadeIn';
import { EXPERIENCE } from '../data/experience';

const ExperienceCard = React.memo(({ job, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative pl-8 md:pl-10"
    >
      {/* Timeline dot + line */}
      <div className="absolute left-0 top-0 bottom-0 flex flex-col items-center">
        <div
          className="w-3 h-3 rounded-full bg-green border-2 border-surface shrink-0 mt-6"
          style={{ boxShadow: '0 0 8px rgba(var(--color-green), 0.3), 0 0 2px rgba(var(--color-green), 0.5)' }}
        />
        {index < EXPERIENCE.length - 1 && (
          <div className="w-px flex-1 bg-gradient-to-b from-green/20 to-border/40 mt-2" />
        )}
      </div>

      <div className="border border-border rounded-xl p-6 bg-raised/60 hover:border-green/20 hover:bg-raised transition-all duration-300 group">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <img
            src={job.logo}
            alt={job.company}
            loading="lazy"
            decoding="async"
            width="40"
            height="40"
            className="w-10 h-10 rounded-lg object-contain bg-subtle border border-border shrink-0 mt-0.5"
            onError={(e) => { e.target.style.visibility = 'hidden'; }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="text-lg font-display font-bold text-txt group-hover:text-txt transition-colors duration-200">
                {job.company}
              </h3>
              <span className="text-sm text-green font-medium">{job.role}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="text-sm text-txt-tertiary font-mono">{job.period}</span>
              <span className="text-xs text-txt-tertiary px-2 py-0.5 bg-subtle/80 rounded-full border border-border/60">
                {job.industry}
              </span>
            </div>
          </div>
        </div>

        {/* Points */}
        <ul className="space-y-2.5 ml-6 sm:ml-14">
          {job.points.map((point, i) => (
            <li key={i} className="flex gap-3 text-sm text-txt-secondary leading-relaxed">
              <span className="text-green/40 mt-0.5 shrink-0 font-mono text-xs">&gt;</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
});

const Experience = () => (
  <FadeIn id="experience" className="py-28 content-auto">
    <div className="max-w-6xl mx-auto px-6">
      <div className="mb-12">
        <span className="text-xs font-mono text-green/50 tracking-widest mb-3 block" aria-hidden="true">02</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">Experience</h2>
        <p className="text-txt-secondary text-lg">Where I've shipped production code</p>
      </div>

      <ol className="space-y-6 max-w-4xl list-none" role="list">
        {EXPERIENCE.map((job, i) => (
          <li key={job.company}>
            <ExperienceCard job={job} index={i} />
          </li>
        ))}
      </ol>
    </div>
  </FadeIn>
);

export default Experience;
